import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { useNotification } from '@/shared/context/NotificationContext';
import { getFirebaseDb } from '@/shared/services/firebase/config';
import { ContentLanguageCode, FIRESTORE_COLLECTIONS, AUDIO_UPLOAD } from '@/shared/constants';
import { getMuseumLanguagesAsContentCodes } from '@/shared/utils/languageUtils';
import { useMuseum } from '@/features/museum/context/MuseumContext';
import { getFirebaseImageUrl } from '@/shared/utils/imageUtils';
import { logger } from '@/shared/utils/logger';
import { sortLanguagesWithFrFirst } from '@/shared/utils/languageUtils';
import { handleFirebaseError } from '@/shared/services/firebase/errors';
import { Work } from '@/shared/types';
import { useOrderManager } from '@/shared/hooks/useOrderManager';
import { useWorks } from '@/features/exhibitions/hooks/useWorks';
import { convertLocationToMultilingual, getAvailableRoomsForFloor } from '../utils/workLocationUtils';
import { saveWork } from '../utils/saveWork';
import { findLanguagesMissingAdTranscription } from '../utils/audioDescription';
import { findLanguagesMissingImageAlt } from '../utils/imageAlt';
import { getAudioFileErrorKey } from '../utils/audioFileValidation';
import { useAudioDescriptionForm } from './useAudioDescriptionForm';

export type WorkDropdown = 'status' | 'order' | 'floor' | 'room';

/** Onglets de la modale épisode (Figma admin 323:5421 → 324:5985). */
export const WORK_SECTIONS = ['metadata', 'cover', 'audioDescription', 'audio'] as const;
export type WorkSection = (typeof WORK_SECTIONS)[number];
export const workSectionTabId = (section: WorkSection) => `work-section-tab-${section}`;
export const WORK_SECTION_PANEL_ID = 'work-section-panel';

export interface UseCreateWorkFormParams {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  museumId: string;
  exhibitionId: string;
  exhibitionActiveLanguages: ContentLanguageCode[];
  exhibitionCurrentLanguage?: ContentLanguageCode;
  work?: Work | null;
}

export const getInitialLanguages = (
  exhibitionActiveLanguages: ContentLanguageCode[]
): ContentLanguageCode[] => {
  return sortLanguagesWithFrFirst(exhibitionActiveLanguages.length > 0 ? exhibitionActiveLanguages : ['fr']);
};

export const useCreateWorkForm = ({
  isOpen,
  onClose,
  onSuccess,
  museumId,
  exhibitionId,
  exhibitionActiveLanguages,
  exhibitionCurrentLanguage,
  work,
}: UseCreateWorkFormParams) => {
  const isEditMode = !!work;
  const { t } = useTranslation();
  const { showError } = useNotification();
  const { museum } = useMuseum();
  const isQrCodeEnabled = museum?.hasQrCode === true;
  const canUseQrCode = isQrCodeEnabled && Boolean(museum?.pwaUrlBase);

  const museumLanguages = getMuseumLanguagesAsContentCodes(museum);

  const initialStatusByLanguage: Record<string, 'draft' | 'published'> = {};
  museumLanguages.forEach((lang) => {
    initialStatusByLanguage[lang] = 'draft';
  });

  const initialLanguage = exhibitionCurrentLanguage || getInitialLanguages(exhibitionActiveLanguages)[0];
  const [currentLanguage, setCurrentLanguage] = useState<ContentLanguageCode>(initialLanguage);
  const [activeLanguages, setActiveLanguages] = useState<ContentLanguageCode[]>(
    getInitialLanguages(exhibitionActiveLanguages)
  );
  const [statusByLanguage, setStatusByLanguage] =
    useState<Record<string, 'draft' | 'published'>>(initialStatusByLanguage);
  const [title, setTitle] = useState<Record<ContentLanguageCode, string>>({} as Record<ContentLanguageCode, string>);
  const [floor, setFloor] = useState<Record<ContentLanguageCode, string>>({} as Record<ContentLanguageCode, string>);
  const [room, setRoom] = useState<Record<ContentLanguageCode, string>>({} as Record<ContentLanguageCode, string>);

  const getAvailableRooms = useCallback(
    () => getAvailableRoomsForFloor(museum?.plans, floor),
    [museum?.plans, floor]
  );

  const isInitialLoadRef = useRef(false);
  const hasUnsavedChangesRef = useRef(false);
  const markDirty = useCallback(() => {
    hasUnsavedChangesRef.current = true;
  }, []);

  useEffect(() => {
    if (!isInitialLoadRef.current) {
      setRoom({} as Record<ContentLanguageCode, string>);
    }
    isInitialLoadRef.current = false;
  }, [floor]);

  const [pillNumber, setPillNumber] = useState<string>('');
  const [order, setOrder] = useState<number>(1);
  const [transcription, setTranscription] = useState<Record<ContentLanguageCode, string>>(
    {} as Record<ContentLanguageCode, string>
  );
  const [saving, setSaving] = useState(false);
  const [allWorks, setAllWorks] = useState<Work[]>([]);

  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [coverRemoved, setCoverRemoved] = useState(false);
  const [imageAlt, setImageAlt] = useState<Record<ContentLanguageCode, string>>(
    {} as Record<ContentLanguageCode, string>
  );
  const [activeSection, setActiveSection] = useState<WorkSection>('metadata');
  const [audioFile, setAudioFile] = useState<Record<ContentLanguageCode, File | null>>(
    {} as Record<ContentLanguageCode, File | null>
  );
  const [audioPreview, setAudioPreview] = useState<Record<ContentLanguageCode, string | null>>(
    {} as Record<ContentLanguageCode, string | null>
  );
  const [audioUploadError, setAudioUploadError] = useState<string | null>(null);
  const [pillNumberError, setPillNumberError] = useState<string | null>(null);

  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [orderDropdownOpen, setOrderDropdownOpen] = useState(false);
  const [floorDropdownOpen, setFloorDropdownOpen] = useState(false);
  const [roomDropdownOpen, setRoomDropdownOpen] = useState(false);
  const [showPublishConfirmation, setShowPublishConfirmation] = useState(false);
  const [showPublishSuccess, setShowPublishSuccess] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);
  const [showWorkQrModal, setShowWorkQrModal] = useState(false);

  const { deleteWork } = useWorks(exhibitionId);

  const coverInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const audioDescription = useAudioDescriptionForm({
    isOpen,
    work,
    languages: activeLanguages,
    currentLanguage,
    markDirty,
  });

  // Chargement à l'ouverture ou au changement d'œuvre seulement : changer de
  // langue (remontée à la page parcours) ne doit ni recharger l'œuvre, ni
  // effacer les modifications en cours, ni revenir à l'onglet Métadonnées.
  const loadedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const loadKey = isOpen ? `${work?.id ?? 'new'}|${museum ? 'museum' : ''}` : null;
    if (loadKey === loadedKeyRef.current) return;
    loadedKeyRef.current = loadKey;
    if (isOpen) {
      hasUnsavedChangesRef.current = false;
      setActiveSection('metadata');
      setCoverRemoved(false);
    }
    if (isOpen && work) {
      isInitialLoadRef.current = true;

      setStatusDropdownOpen(false);
      setOrderDropdownOpen(false);
      setFloorDropdownOpen(false);
      setRoomDropdownOpen(false);
      setAudioUploadError(null);
      setPillNumberError(null);

      const initialLanguages = getInitialLanguages(exhibitionActiveLanguages);
      setActiveLanguages(initialLanguages);
      const langToUse = exhibitionCurrentLanguage || initialLanguages[0];
      setCurrentLanguage(langToUse);

      const statusData: Record<ContentLanguageCode, 'draft' | 'published'> = {} as Record<
        ContentLanguageCode,
        'draft' | 'published'
      >;
      initialLanguages.forEach((lang) => {
        statusData[lang] = work.status?.[lang] || 'draft';
      });
      setStatusByLanguage(statusData);
      setTitle(work.name || {});

      const floorData = convertLocationToMultilingual(work.floor, initialLanguages, 'floor', museum?.plans);
      const roomData = convertLocationToMultilingual(work.room, initialLanguages, 'room', museum?.plans, floorData);

      setFloor(floorData);
      if (Object.keys(roomData).length > 0) {
        setRoom(roomData);
      }

      setPillNumber(work.pillNumber?.toString() || '');
      setOrder(work.order || 1);
      setTranscription(work.description || {});
      setImageAlt((work.imageAlt || {}) as Record<ContentLanguageCode, string>);

      if (work.imageUrl) {
        (async () => {
          try {
            const imageUrl = await getFirebaseImageUrl(work.imageUrl!);
            setCoverPreview(imageUrl);
          } catch (error) {
            logger.error("Erreur lors du chargement de l'image:", error);
          }
        })();
      }

      if (work.audioUrl) {
        (async () => {
          const audioPreviews: Record<ContentLanguageCode, string | null> = {} as Record<
            ContentLanguageCode,
            string | null
          >;
          for (const lang of initialLanguages) {
            if (work.audioUrl[lang]) {
              try {
                const audioUrl = await getFirebaseImageUrl(work.audioUrl[lang]);
                audioPreviews[lang] = audioUrl;
              } catch (error) {
                logger.error(`Erreur lors du chargement de l'audio pour ${lang}:`, error);
              }
            }
          }
          setAudioPreview(audioPreviews);
        })();
      }
    } else if (isOpen && !work) {
      const initialLanguages = getInitialLanguages(exhibitionActiveLanguages);
      const langToUse = exhibitionCurrentLanguage || initialLanguages[0];
      setCurrentLanguage(langToUse);
      setActiveLanguages(initialLanguages);

      setStatusDropdownOpen(false);
      setOrderDropdownOpen(false);
      setFloorDropdownOpen(false);
      setRoomDropdownOpen(false);
      setPillNumberError(null);
      const initialStatus: Record<ContentLanguageCode, 'draft' | 'published'> = {} as Record<
        ContentLanguageCode,
        'draft' | 'published'
      >;
      initialLanguages.forEach((lang) => {
        initialStatus[lang] = 'draft';
      });
      setStatusByLanguage(initialStatus);
      setTitle({} as Record<ContentLanguageCode, string>);
      setFloor({} as Record<ContentLanguageCode, string>);
      setRoom({} as Record<ContentLanguageCode, string>);
      setPillNumber('');
      setTranscription({} as Record<ContentLanguageCode, string>);
      setImageAlt({} as Record<ContentLanguageCode, string>);
      setCoverImage(null);
      setCoverPreview(null);
      setAudioFile({} as Record<ContentLanguageCode, File | null>);
      setAudioPreview({} as Record<ContentLanguageCode, string | null>);
      setAudioUploadError(null);
      isInitialLoadRef.current = false;
    }
  }, [isOpen, work, exhibitionActiveLanguages, exhibitionCurrentLanguage, museum?.plans, museum]);

  const loadAllWorks = useCallback(async (): Promise<Work[] | void> => {
    if (!museum?.id || !exhibitionId) {
      return;
    }

    const db = getFirebaseDb();
    if (!db) {
      return;
    }

    try {
      const worksRef = collection(
        db,
        FIRESTORE_COLLECTIONS.MUSEUMS,
        museum.id,
        FIRESTORE_COLLECTIONS.EXHIBITIONS,
        exhibitionId,
        FIRESTORE_COLLECTIONS.WORKS
      );
      const q = query(worksRef, orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const worksData = querySnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      })) as Work[];

      setAllWorks(worksData);
      return worksData;
    } catch (error) {
      logger.error('Erreur lors de la récupération de toutes les œuvres:', error);
    }
  }, [museum?.id, exhibitionId]);

  useEffect(() => {
    if (!isOpen || !museum?.id || !exhibitionId) return;

    loadAllWorks().then((worksData) => {
      if (!work && worksData) {
        setOrder(worksData.length + 1);
      }
    });
  }, [isOpen, museum?.id, exhibitionId, loadAllWorks, work]);

  useEffect(() => {
    const raw = pillNumber.trim();
    if (raw === '' || raw === '-') {
      setPillNumberError(null);
      return;
    }
    const num = parseInt(raw, 10);
    if (Number.isNaN(num)) {
      setPillNumberError(null);
      return;
    }
    const conflicting = allWorks.find(
      (w) => w.id !== work?.id && (w.pillNumber === num || w.pillNumber?.toString() === raw)
    );
    if (conflicting) {
      const episodeTitle = conflicting.name?.[currentLanguage] || conflicting.name?.fr || t('exhibitions.noTitle');
      setPillNumberError(t('exhibitions.pillNumberAlreadyUsed', { title: episodeTitle }));
    } else {
      setPillNumberError(null);
    }
  }, [pillNumber, allWorks, work?.id, currentLanguage, t]);

  const { changeOrder: changeWorkOrder } = useOrderManager<Work>({
    museumId: museum?.id || '',
    collectionPath: [FIRESTORE_COLLECTIONS.EXHIBITIONS, exhibitionId, FIRESTORE_COLLECTIONS.WORKS],
    onSuccess: () => {
      loadAllWorks();
    },
    onError: (error) => {
      showError(error);
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.dropdown')) {
        setStatusDropdownOpen(false);
        setOrderDropdownOpen(false);
        setFloorDropdownOpen(false);
        setRoomDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const closeAllDropdownsExcept = (openDropdown: WorkDropdown | null) => {
    if (openDropdown !== 'status') setStatusDropdownOpen(false);
    if (openDropdown !== 'order') setOrderDropdownOpen(false);
    if (openDropdown !== 'floor') setFloorDropdownOpen(false);
    if (openDropdown !== 'room') setRoomDropdownOpen(false);
  };

  const handleCoverClick = () => {
    coverInputRef.current?.click();
  };

  const handleAudioClick = () => {
    audioInputRef.current?.click();
  };

  const handleCoverSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      markDirty();
      setCoverImage(file);
      setCoverRemoved(false);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCoverModify = (e: React.MouseEvent) => {
    e.stopPropagation();
    coverInputRef.current?.click();
  };

  const handleCoverDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    markDirty();
    setCoverImage(null);
    setCoverPreview(null);
    setCoverRemoved(true);
    if (coverInputRef.current) {
      coverInputRef.current.value = '';
    }
  };

  const handleAudioSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const errorKey = getAudioFileErrorKey(file);
    if (errorKey === 'format') {
      setAudioUploadError(t('exhibitions.audioUploadFormatError'));
      return;
    }
    if (errorKey === 'size') {
      setAudioUploadError(t('exhibitions.audioUploadSizeError', { maxSize: AUDIO_UPLOAD.MAX_SIZE_MB }));
      return;
    }

    setAudioUploadError(null);
    markDirty();
    setAudioFile({
      ...audioFile,
      [currentLanguage]: file,
    });
    const url = URL.createObjectURL(file);
    setAudioPreview({
      ...audioPreview,
      [currentLanguage]: url,
    });
  };

  const handleRemoveAudio = () => {
    setAudioUploadError(null);
    markDirty();
    const currentPreview = audioPreview[currentLanguage];
    if (currentPreview) {
      URL.revokeObjectURL(currentPreview);
    }
    setAudioFile({
      ...audioFile,
      [currentLanguage]: null,
    });
    setAudioPreview({
      ...audioPreview,
      [currentLanguage]: null,
    });
    if (audioInputRef.current) {
      audioInputRef.current.value = '';
    }
  };

  const hasAudioForCurrentLanguage = () => Boolean(audioPreview[currentLanguage]);

  const formatLanguages = (languages: string[]) => languages.map((lang) => lang.toUpperCase()).join(', ');

  /** En cas d'erreur, ouvre l'onglet à compléter. */
  const validateWorkForm = (): boolean => {
    if (!hasAudioForCurrentLanguage()) {
      setActiveSection('audio');
      showError(t('exhibitions.audioRequiredError'));
      return false;
    }
    const missingAlt = findLanguagesMissingImageAlt({
      languages: activeLanguages,
      hasImage: Boolean(coverPreview),
      imageAlt,
      status: statusByLanguage,
    });
    if (missingAlt.length > 0) {
      setActiveSection('cover');
      showError(t('works.cover.missingAlt', { languages: formatLanguages(missingAlt) }));
      return false;
    }
    const missingAd = findLanguagesMissingAdTranscription({
      languages: activeLanguages,
      hasAudio: audioDescription.hasAudio,
      transcription: audioDescription.allTranscriptions,
      status: statusByLanguage,
    });
    if (missingAd.length > 0) {
      setActiveSection('audioDescription');
      showError(
        t('exhibitions.audioDescription.missingTranscription', {
          languages: formatLanguages(missingAd),
        }),
      );
      return false;
    }
    return true;
  };

  const handleClose = () => {
    setShowUnsavedConfirm(false);
    const initialLanguages = getInitialLanguages(exhibitionActiveLanguages);
    setCoverImage(null);
    setCoverPreview(null);
    setCoverRemoved(false);
    setImageAlt({} as Record<ContentLanguageCode, string>);
    setActiveSection('metadata');
    setAudioUploadError(null);
    Object.values(audioPreview).forEach((url) => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    });
    setAudioFile({} as Record<ContentLanguageCode, File | null>);
    setAudioPreview({} as Record<ContentLanguageCode, string | null>);
    setTranscription({} as Record<ContentLanguageCode, string>);
    audioDescription.reset();
    const resetStatusByLanguage: Record<string, 'draft' | 'published'> = {};
    museumLanguages.forEach((lang) => {
      resetStatusByLanguage[lang] = 'draft';
    });
    setStatusByLanguage(resetStatusByLanguage);
    setShowPublishConfirmation(false);
    setShowPublishSuccess(false);
    setShowDeleteConfirmation(false);
    setShowWorkQrModal(false);
    setTitle({} as Record<ContentLanguageCode, string>);
    setFloor({} as Record<ContentLanguageCode, string>);
    setRoom({} as Record<ContentLanguageCode, string>);
    setPillNumber('-');
    setOrder(1);
    setCurrentLanguage(initialLanguages[0]);
    setActiveLanguages(initialLanguages);
    setStatusDropdownOpen(false);
    setOrderDropdownOpen(false);
    setFloorDropdownOpen(false);
    setRoomDropdownOpen(false);
    onClose();
  };

  const performSave = async (isPublishing: boolean = false) => {
    if (!validateWorkForm()) {
      return;
    }
    if (!museumId || !exhibitionId) {
      showError(t('exhibitions.exhibitionNotFound'));
      return;
    }
    if (pillNumberError) {
      showError(pillNumberError);
      return;
    }

    setSaving(true);
    try {
      const result = await saveWork({
        museumId,
        exhibitionId,
        work,
        isEditMode,
        order,
        allWorks,
        coverImage,
        coverRemoved,
        imageAlt,
        audioFile,
        activeLanguages,
        title,
        transcription,
        statusByLanguage,
        floor,
        room,
        pillNumber,
        audioDescription: audioDescription.toSaveInput(),
        museumPlans: museum?.plans,
        changeWorkOrder,
        loadAllWorks,
      });

      if (!result.ok) {
        if (result.code === 'database') {
          showError(t('exhibitions.databaseError'));
        } else if (result.code === 'order') {
          showError(t('exhibitions.updateOrderError'));
        } else if (result.code === 'validation') {
          result.errors.forEach((error) => showError(error));
        }
        return;
      }

      if (isPublishing) {
        setShowPublishSuccess(true);
      } else {
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      logger.error("Erreur lors de la création de l'œuvre:", error);
      showError(handleFirebaseError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!validateWorkForm()) {
      return;
    }

    if (statusByLanguage[currentLanguage] === 'published') {
      setShowPublishConfirmation(true);
      return;
    }

    await performSave();
  };

  const handleConfirmPublish = async () => {
    setShowPublishConfirmation(false);
    await performSave(true);
  };

  const handleCancelPublish = () => {
    setShowPublishConfirmation(false);
  };

  const handleCloseSuccess = () => {
    setShowPublishSuccess(false);
    handleClose();
    if (onSuccess) {
      onSuccess();
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  const handleConfirmDelete = async () => {
    if (!work?.id) {
      showError(t('works.noWorks'));
      return;
    }

    setDeleting(true);
    try {
      const success = await deleteWork(work.id);
      if (success) {
        setShowDeleteConfirmation(false);
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        showError(t('exhibitions.saveError'));
      }
    } catch (error) {
      logger.error("Erreur lors de la suppression de l'œuvre:", error);
      showError(handleFirebaseError(error));
    } finally {
      setDeleting(false);
    }
  };

  const handleRequestClose = () => {
    if (hasUnsavedChangesRef.current) {
      setShowUnsavedConfirm(true);
    } else {
      handleClose();
    }
  };

  return {
    audioDescription,
    activeSection,
    setActiveSection,
    imageAlt,
    setImageAlt,
    isEditMode,
    museum,
    isQrCodeEnabled,
    canUseQrCode,
    currentLanguage,
    setCurrentLanguage,
    activeLanguages,
    statusByLanguage,
    setStatusByLanguage,
    title,
    setTitle,
    floor,
    setFloor,
    room,
    setRoom,
    pillNumber,
    setPillNumber,
    order,
    setOrder,
    transcription,
    setTranscription,
    saving,
    allWorks,
    coverPreview,
    audioPreview,
    audioUploadError,
    coverInputRef,
    audioInputRef,
    statusDropdownOpen,
    setStatusDropdownOpen,
    orderDropdownOpen,
    setOrderDropdownOpen,
    floorDropdownOpen,
    setFloorDropdownOpen,
    roomDropdownOpen,
    setRoomDropdownOpen,
    showPublishConfirmation,
    showPublishSuccess,
    showDeleteConfirmation,
    deleting,
    showUnsavedConfirm,
    setShowUnsavedConfirm,
    showWorkQrModal,
    setShowWorkQrModal,
    getAvailableRooms,
    markDirty,
    closeAllDropdownsExcept,
    handleCoverClick,
    handleAudioClick,
    handleCoverSelect,
    handleCoverModify,
    handleCoverDelete,
    handleAudioSelect,
    handleRemoveAudio,
    handleSave,
    handleConfirmPublish,
    handleCancelPublish,
    handleCloseSuccess,
    handleDeleteClick,
    handleCancelDelete,
    handleConfirmDelete,
    handleRequestClose,
    handleClose,
  };
};
